import React, { useEffect, useRef, useState } from 'react';
import { getTranslateByKey } from '@/main/services/utils';

interface Props {
  style?: React.CSSProperties;
  finishTime: string;
  showExpire?: boolean;
  onTimeFinished?: () => void;
}
/**
  Component that displays the remaining time until the end of an auction.
  @param {string} finishTime - The time when the auction ends.
  @param {boolean} showExpire - Whether to display the expired text.
  @param {Function} onTimeFinished - Function to be executed when the time is up.
  @example
  <RemainingTimeComponent finishTime="2023-08-03T12:00:00" showExpire onTimeFinished={() => ()} />
*/
export default function RemainingTimeComponent(props: Props) {
  const [remainingTime, setRemainingTime] = useState<string>('');
  const isTimeUpRef = useRef<boolean>(false);

  useEffect(() => {
    isTimeUpRef.current = false;
    let intervalId: NodeJS.Timeout;

    function calculateRemainingTime() {
      const now = new Date();
      const finishTime = new Date(props.finishTime);
      const nowInUtc = new Date(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds()
      );
      const timeDifference = finishTime.getTime() - nowInUtc.getTime();

      if (timeDifference <= 0 && !isTimeUpRef.current) {
        clearInterval(intervalId);
        isTimeUpRef.current = true;

        if (props.onTimeFinished) {
          props.onTimeFinished();
        }
      } else if (timeDifference > 0) {
        const totalSeconds = Math.floor(timeDifference / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0) {
          setRemainingTime(`${hours}h ${minutes}m ${seconds}s`);
        } else if (minutes > 0) {
          setRemainingTime(`${minutes}m ${seconds}s`);
        } else {
          setRemainingTime(`${seconds}s`);
        }
      }
    }

    calculateRemainingTime();
    intervalId = setInterval(calculateRemainingTime, 1000);

    return () => clearInterval(intervalId);
  }, [props.finishTime, props.onTimeFinished]);

  if (props.showExpire && !remainingTime)
    return <div>{getTranslateByKey('information.expired')}</div>;

  return <div style={props.style}>{remainingTime}</div>;
}
